/**
 * LITE (r14, plans/portrait-phone.md L1–L16): the measured ladder above the silent render-scale governor.
 *
 *   rung 1  30 fps cap (the whole frame skips, never single panels)
 *   rung 2  render scale r up to the device pixel ratio (GL at 1 CSS px; the HUD stays native)
 *   rung 3  battle-cam inset tiles one level coarser
 *
 * Pure logic, fed with frame times and a clock injected by the caller (no wall clock here): the *rung* is the
 * input to the frame. A rung is added when the p95 frame time stays over budget for 5 s (with the governor at its
 * cap); the highest rung is released after 2 min within budget at the predicted lower cost, and a rung that trips
 * again soon after its release doubles the next wait (2 → 4 → 8 min).
 */

export type LiteSetting = 'auto' | 'on' | 'off';
export const LITE_MAX_RUNG = 3;
/** seconds over budget before the next rung */
export const LITE_UP_SEC = 5;
/** seconds within budget before releasing a rung: 2, 4, 8 min */
export const LITE_DOWN_SEC: readonly number[] = [120, 240, 480];
/** a re-trip within this many seconds of a release counts as "the same rung tripping again" */
const RETRIP_SEC = 60;

/** The frame budget at 60 fps and the one when the platform caps rAF at 30 (33.3 ms plus 10% rAF jitter margin). */
export const BUDGET_60 = 16.7;
export const BUDGET_30 = 36.7;

export function parseLiteSetting(v: string | null | undefined): LiteSetting | null {
  const s = (v ?? '').trim().toLowerCase();
  if (s === '1' || s === 'on') return 'on';
  if (s === '0' || s === 'off') return 'off';
  return s === 'auto' ? 'auto' : null;
}

/** median frame work under this (ms) is light enough for ~33 ms intervals to be the platform's cap, not our cost */
const WORK_LIGHT_MS = 12;

/** Sorted-copy percentile over a ring of ms values (allocation-free after construction). */
class Ring {
  private readonly win: Float64Array;
  private readonly sorted: Float64Array;
  private n = 0;
  private head = 0;
  constructor(size: number) {
    this.win = new Float64Array(size);
    this.sorted = new Float64Array(size);
  }
  get count(): number {
    return this.n;
  }
  get size(): number {
    return this.win.length;
  }
  push(v: number): void {
    this.win[this.head] = v;
    this.head = (this.head + 1) % this.win.length;
    if (this.n < this.win.length) this.n++;
  }
  clear(): void {
    this.n = 0;
    this.head = 0;
  }
  percentile(q: number): number {
    const n = this.n;
    if (n === 0) return 0;
    const s = this.sorted;
    for (let i = 0; i < s.length; i++) s[i] = i < n ? (this.win[i] ?? 0) : Infinity;
    s.sort();
    return s[Math.min(n - 1, Math.ceil(q * n) - 1)] ?? 0;
  }
}

/**
 * H5: the frame budget from the steady rAF interval (every callback, including the ones LITE skips). A median near
 * 33 ms means the platform caps rAF (iOS Low Power Mode): the budget is the 30 fps one. A 120 Hz display stays at 60:
 * the target is 60, not the display rate.
 */
export class RafBudget {
  private readonly ring = new Ring(60);
  private readonly work = new Ring(60);
  private pushes = 0;
  private med = BUDGET_60;
  /** the silent governor is at its cap (or pinned): only then can ~33 ms intervals be called a cap */
  governorAtCap = false;
  private capCandidate = false;

  /** A platform cap, or a GPU-bound device the governor has nothing left for: 30 fps is right either way. */
  get platformCapped(): boolean {
    return this.capCandidate && this.governorAtCap;
  }

  /** the CPU work time of one rendered frame (ms): GPU time is invisible, but a platform cap leaves it small */
  pushWork(ms: number): void {
    if (ms >= 0) this.work.push(ms);
  }

  push(intervalMs: number): void {
    if (!(intervalMs > 0)) return;
    this.ring.push(intervalMs);
    if (++this.pushes % 15 !== 0 || this.ring.count < 30) return;
    this.med = this.ring.percentile(0.5);
    // a slow device also shows ~33 ms intervals: only call it a platform cap while the frame work is clearly light
    this.capCandidate = this.med >= 28 && this.med <= 40 && this.work.count >= 10 && this.work.percentile(0.5) < WORK_LIGHT_MS;
  }
  /** the steady rAF interval, ms (16.7 until measured) */
  get intervalMs(): number {
    return this.med;
  }
  get budgetMs(): number {
    return this.platformCapped ? BUDGET_30 : BUDGET_60;
  }
}

export interface LiteInput {
  /** the budget for the frame times being fed (BUDGET_30 while any rung or the platform caps at 30) */
  budgetMs: number;
  /** the render-scale governor is at its cap (or pinned) */
  atCap: boolean;
  /** (r_now / r_lower)² when rung 2 is the one to release, else 1: the fill-bound cost model of the governor */
  lowerCost: number;
}

export class LiteLadder {
  private setting: LiteSetting;
  private _rung = 0;
  private platform = false;
  private readonly ring = new Ring(60);
  private overSince = Number.NaN;
  private underSince = Number.NaN;
  private waitIdx = 0;
  private lastRelease = { rung: 0, at: Number.NEGATIVE_INFINITY };
  /** automatic rung changes (for tests and the debug overlay) */
  changes = 0;

  constructor(setting: LiteSetting = 'auto') {
    this.setting = setting;
    this.apply();
  }

  /** the rung in effect: 0 = off, 1 = 30 fps, 2 = + r to dpr, 3 = + softer battle cam */
  get rung(): number {
    return this._rung;
  }
  get mode(): LiteSetting {
    return this.setting;
  }
  /** the platform caps rAF at 30: rung 1 counts as active (and can't be released), with no notice */
  get platformCapped(): boolean {
    return this.platform;
  }
  get p95(): number {
    return this.ring.percentile(0.95);
  }

  setMode(m: LiteSetting): void {
    this.setting = m;
    this._rung = 0; // only automatic steps hold a rung: a switch to auto starts at the floor
    this.apply();
  }

  setPlatformCapped(b: boolean): void {
    if (b === this.platform) return;
    this.platform = b;
    this.apply();
  }

  private apply(): void {
    this.ring.clear();
    this.overSince = Number.NaN;
    this.underSince = Number.NaN;
    if (this.setting === 'on') this._rung = LITE_MAX_RUNG;
    else if (this.setting === 'off') this._rung = 0;
    else this._rung = Math.max(this._rung, this.platform ? 1 : 0);
  }

  /** Feed one rendered frame's interval (ms) at caller-clock `nowSec`; returns the rung. */
  sample(frameMs: number, nowSec: number, inp: LiteInput): number {
    if (this.setting !== 'auto') return this._rung;
    this.ring.push(frameMs);
    if (this.ring.count < 30) return this._rung;
    const p95 = this.ring.percentile(0.95);
    const floor = this.platform ? 1 : 0;
    if (p95 > inp.budgetMs) {
      this.underSince = Number.NaN;
      if (this._rung === 0 && !inp.atCap) {
        this.overSince = Number.NaN; // the governor still has room: it acts first
        return this._rung;
      }
      if (Number.isNaN(this.overSince)) this.overSince = nowSec;
      if (nowSec - this.overSince >= LITE_UP_SEC && this._rung < LITE_MAX_RUNG) {
        const next = this._rung + 1;
        if (next === this.lastRelease.rung && nowSec - this.lastRelease.at <= RETRIP_SEC) {
          this.waitIdx = Math.min(this.waitIdx + 1, LITE_DOWN_SEC.length - 1);
        }
        this._rung = next;
        this.changes++;
        this.ring.clear();
        this.overSince = Number.NaN;
      }
    } else {
      this.overSince = Number.NaN;
      if (this._rung <= floor) {
        this.underSince = Number.NaN;
        return this._rung;
      }
      if (Number.isNaN(this.underSince)) this.underSince = nowSec;
      if (nowSec - this.underSince >= (LITE_DOWN_SEC[this.waitIdx] ?? 480)) {
        // rung 1 (30 fps) hides its own cost, so it has no prediction to check; rung 2 uses the governor's model
        if (this._rung !== 2 || p95 * inp.lowerCost <= 0.85 * inp.budgetMs) {
          this.lastRelease = { rung: this._rung, at: nowSec };
          this._rung--;
          this.changes++;
          this.ring.clear();
        }
        this.underSince = nowSec;
      }
    }
    return this._rung;
  }
}
